/* eslint-disable @typescript-eslint/no-explicit-any */
// src/api/services/players.service.ts
import { apiClient } from "@/api/client";
import type {
  Player,
  CreatePlayerRequest,
  UpdatePlayerRequest,
  PlayerFilters,
  Pagination,
} from "@/types/api.types";
import { API_ENDPOINTS } from "@/api/endpoints";

export const playersService = {
  // Get all players with filters
  getAllPlayers: async (
    filters?: PlayerFilters,
  ): Promise<{ players: Player[]; pagination: Pagination }> => {
    const params = new URLSearchParams();

    if (filters?.search) params.append("search", filters.search);
    if (filters?.nationId)
      params.append("nationId", filters.nationId.toString());
    if (filters?.position) params.append("position", filters.position);
    if (filters?.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());
    if (filters?.club) params.append("club", filters.club);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    return await apiClient.get(
      `${API_ENDPOINTS.PLAYERS.LIST}?${params.toString()}`,
    );
  },

  // Get single player
  getPlayer: async (id: number): Promise<Player> => {
    return await apiClient.get(API_ENDPOINTS.PLAYERS.DETAIL(id));
  },

  // Create player
  createPlayer: async (data: CreatePlayerRequest): Promise<Player> => {
    return await apiClient.post(API_ENDPOINTS.PLAYERS.CREATE, data);
  },

  // Update player
  updatePlayer: async (
    id: number,
    data: UpdatePlayerRequest,
  ): Promise<Player> => {
    return await apiClient.patch(API_ENDPOINTS.PLAYERS.UPDATE(id), data);
  },

  // Delete player
  deletePlayer: async (id: number): Promise<{ message: string }> => {
    return await apiClient.delete(API_ENDPOINTS.PLAYERS.DELETE(id));
  },

  // Activate player
  activatePlayer: async (id: number): Promise<Player> => {
    return await apiClient.patch(API_ENDPOINTS.PLAYERS.ACTIVATE(id));
  },

  // Deactivate player
  deactivatePlayer: async (id: number): Promise<Player> => {
    return await apiClient.patch(API_ENDPOINTS.PLAYERS.DEACTIVATE(id));
  },

  // Get teams using this player
  getPlayerTeams: async (id: number): Promise<any[]> => {
    return await apiClient.get(API_ENDPOINTS.PLAYERS.TEAMS(id));
  },

  // Upload player photo to Cloudinary
  uploadPhoto: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("file", file);

    return await apiClient.post(API_ENDPOINTS.PLAYERS.UPLOAD_PHOTO, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};
